import { useMsal } from '@azure/msal-react';
import { Box, Button, DialogActions, Divider, FormControlLabel, FormGroup, Grid, Paper, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react'
import AccountClient from '../apiClients/AccountClient';
import BusySpinner from '../components/BusySpinner';
import { useErrorDisplay } from '../components/ErrorDisplay';
import { IsEmpty, ValidateEmail } from '../Helpers';
import Checkbox from '@mui/material/Checkbox';
import useViewportMode from '../hooks/useViewportMode';


export interface PredefinedPositionCheckboxProps {
    display: string;
    position: string;
    checked: boolean;
    id: string;
}

const numberOfLastStep = 4;
const minHeightForBody = '200px';

const CreateAccountPage = () => {
    const theme = useTheme();
    const { mode: viewportMode, isPhone, isDesktop } = useViewportMode();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [step, setStep] = useState<number>(1);
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [organization, setOrganization] = useState<string>();
    const [password, setPassword] = useState<string>();
    const [password2, setPassword2] = useState<string>();
    const [showValidation, setShowValidation] = useState<boolean>();

    const [predefinedTemplates, setPredefinedTemplates] = useState<PredefinedPositionCheckboxProps[]>([]);

    const { instance } = useMsal();

    useEffect(() => {
        const fetchData = async () => {
            const t = [
                { display: 'Accounting Professional'
                , position: 'AccountingProfessional'
                , checked: true
                , id: '1' },
            ]
    
            setPredefinedTemplates(t);
    
            // const templates = await new MarketplaceClient().GetTemplatesFiltered('predefined');

            // const checkboxTemplates = templates.map(t => {
            //     return {
            //         display: t.Name,
            //         position: t.PositionId,
            //         checked: false,
            //         id: t.Id
            //     }
            // });
        }
    
        fetchData();
    }, []);
    

    const step1_validated = () => {
        return !(IsEmpty(firstName) || IsEmpty(lastName) || IsEmpty(organization))
    }

    const step2_validated = () => {
        return !(IsEmpty(password) || IsEmpty(password2) || IsEmpty(email) || password !== password2)
    }

    const handleBackButton = () => {
        let currentStep = step;
        if (currentStep > 1)
            setStep(currentStep - 1);
    }

    const handlePositionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let temp = predefinedTemplates.map(pt => {
            var returnArray = { ...pt };

            if (pt.position === event.target.id)
                returnArray.checked = event.target.checked;

            return returnArray;
        })

        setPredefinedTemplates([...temp]);
    }

    const createAccount = async () => {
        try {
            setBusy(true);

            var response = await new AccountClient().CreateOrganization({ FirstName: firstName, LastName: lastName, Email: email, Password: password, OrganizationName: organization, Positions: predefinedTemplates.filter(f => f.checked).map(m => m.position) });
            if (response.IsError) {
                setError(response.Errors[0], "There was an error creating your account");
            }
            else {
                setStep(4);
            }
        } catch (error) {
            setError(error, "Failed to create the account");
        } finally {
            setBusy(false);
        }
    }

    const tryMoveNextStep = async () => {
        setShowValidation(false);
        if (step === 1 && step1_validated())
            setStep(2);
        else if (step === 2 && step2_validated())
            setStep(3);
        else if (step === 3)
            await createAccount();
        else
            setShowValidation(true);
    }
    const accountCardSx = {
        borderRadius: '5px',
        ...(!isDesktop && {
            width: '100%',
            '& .MuiInputBase-root, & .MuiButtonBase-root': { minHeight: 44 },
        }),
    };
    const bodySx = isDesktop
        ? { margin: '15px', minHeight: minHeightForBody }
        : { m: 0, p: 2, minHeight: 0 };
    const actionSx = !isDesktop ? {
        px: 2,
        py: 1.5,
        pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        flexDirection: isPhone ? 'column' : 'row',
        gap: 1,
        '& > :not(:first-of-type)': { marginLeft: 0 },
        '& .MuiButton-root': { minHeight: 44, width: isPhone ? '100%' : 'auto' },
    } : undefined;
    return (
        <>
            <ErrorDisplay />
            <Box data-viewport-mode={viewportMode} sx={!isDesktop ? {
                minHeight: '100vh',
                '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
                overflowY: 'auto',
                px: isPhone ? 1.5 : 3,
                pt: isPhone ? 1.5 : 3,
                pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            } : undefined}>
            <Grid container spacing={2} justifyContent={!isDesktop ? 'center' : undefined}>
                {isDesktop && <Grid item xs={2} sm={2}></Grid>}
                <Grid item xs={12} sm={isDesktop ? 8 : 10} md={isDesktop ? undefined : 8} alignItems='center' sx={{ maxWidth: !isDesktop ? 760 : undefined }}>
                    <BusySpinner busy={busy}>
                        {step === 1 ?
                            <Paper sx={accountCardSx} elevation={5}>
                                <Box component='div' sx={{
                                    borderRadius: '5px 5px 0px 0px',
                                    width: '100%',
                                    padding: '0px 15px 15px 15px',
                                    backgroundColor: theme.palette.primary.main
                                }}>
                                    <h1 style={{ margin: '0px' }}>Create Account</h1>
                                    <h4 style={{ margin: '0px' }}>Step {step} of {numberOfLastStep}: Name and Organization</h4>
                                </Box>
                                <Divider />
                                <Box sx={bodySx}>
                                    <TextField label="First Name"
                                        fullWidth
                                        variant="outlined"
                                        value={firstName}
                                        error={showValidation && IsEmpty(firstName)}
                                        onChange={(e) => setFirstName(e.target.value)} />
                                    <div style={{ height: '15px' }} />
                                    <TextField label="Last Name"
                                        fullWidth
                                        variant="outlined"
                                        value={lastName}
                                        error={showValidation && IsEmpty(lastName)}
                                        onChange={(e) => setLastName(e.target.value)} />
                                    <div style={{ height: '15px' }} />
                                    <TextField label="Organization"
                                        fullWidth
                                        variant="outlined"
                                        value={organization}
                                        error={showValidation && IsEmpty(organization)}
                                        onChange={(e) => setOrganization(e.target.value)} />
                                </Box>
                                <Divider />
                                <DialogActions sx={actionSx}>
                                    <Button disabled={busy} onClick={tryMoveNextStep} color="inherit">Next</Button>
                                </DialogActions>
                            </Paper>
                            : null}
                        {step === 2 ?
                            <Paper sx={accountCardSx} elevation={5}>
                                <Box component='div' sx={{
                                    borderRadius: '5px 5px 0px 0px',
                                    width: '100%',
                                    padding: '0px 15px 15px 15px',
                                    backgroundColor: theme.palette.primary.main
                                }}>
                                    <h1 style={{ margin: '0px', marginTop: '10px' }}>Create Account</h1>
                                    <h4 style={{ margin: '0px' }}>Step {step} of {numberOfLastStep}: Email and password</h4>
                                </Box>
                                <Box sx={bodySx}>
                                    <TextField label="Email"
                                        fullWidth
                                        variant="outlined"
                                        value={email}
                                        error={showValidation && !ValidateEmail(email)}
                                        onChange={(e) => setEmail(e.target.value)} />
                                    <div style={{ height: '15px' }} />
                                    <TextField label="Password"
                                        fullWidth
                                        variant="outlined"
                                        type="password"
                                        error={showValidation && IsEmpty(password)}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)} />
                                    <div style={{ height: '15px' }} />
                                    <TextField label="Validate Password"
                                        fullWidth
                                        variant="outlined"
                                        type="password"
                                        error={showValidation && (IsEmpty(password2) || password2 !== password)} value={password2}
                                        onChange={(e) => setPassword2(e.target.value)} />
                                </Box>
                                <Divider />
                                <DialogActions sx={actionSx}>
                                    <Button disabled={busy} onClick={handleBackButton} color="primary">Back</Button>
                                    <Button disabled={busy} onClick={tryMoveNextStep} color="inherit">Next</Button>
                                </DialogActions>
                            </Paper>
                            : null}
                        {step === 3 ?
                            <Paper sx={accountCardSx} elevation={5}>
                                <Box component='div' sx={{
                                    borderRadius: '5px 5px 0px 0px',
                                    width: '100%',
                                    padding: '0px 15px 15px 15px',
                                    backgroundColor: theme.palette.primary.main
                                }}>
                                    <h1 style={{ margin: '0px', marginTop: '10px' }}>Create Account</h1>
                                    <h4 style={{ margin: '0px' }}>Step {step} of {numberOfLastStep}: Select starter position templates</h4>
                                </Box>
                                <Box sx={bodySx}>
                                    <FormGroup>
                                        {predefinedTemplates.map((predefined) => {
                                            return <FormControlLabel
                                                control={
                                                    <Checkbox id={predefined.position} checked={predefined.checked} onChange={handlePositionChange} />}
                                                label={predefined.display} />
                                        })}
                                    </FormGroup>
                                </Box>
                                <Divider />
                                <DialogActions sx={actionSx}>
                                    <Button disabled={busy} onClick={handleBackButton} color="primary">Back</Button>
                                    <Button disabled={busy} onClick={tryMoveNextStep} color="inherit">Create Account</Button>
                                </DialogActions>
                            </Paper>
                            : null}
                        {step === 4 ?
                            <Paper sx={accountCardSx} elevation={5}>
                                <Box component='div' sx={{
                                    borderRadius: '5px 5px 0px 0px',
                                    width: '100%',
                                    padding: '0px 15px 15px 15px',
                                    backgroundColor: theme.palette.primary.main
                                }}>
                                    <h1 style={{ margin: '0px' }}>Create Account</h1>
                                </Box>
                                <Box sx={bodySx}>
                                    <Typography paragraph>
                                        Thank you for creating your account, please login by clicking on the login button at the top.
                                    </Typography>
                                </Box>
                                <Divider />
                                <DialogActions sx={actionSx}>
                                    <Button disabled={busy} onClick={() => instance.loginRedirect()} color="primary">Login</Button>
                                </DialogActions>
                            </Paper>
                            : null}
                    </BusySpinner>
                </Grid>
                {isDesktop && <Grid item xs={2} sm={2}></Grid>}
            </Grid>
            </Box>
        </>
    );
}

export default CreateAccountPage
